// =============================================================================
// domain/accountFee.ts — what an account cost, and the ONE ledger row that says so.
// -----------------------------------------------------------------------------
// Relocated from the old client's finish-mode save (app/index.html:47686-47745).
//
// ── 🚨 WHY THIS IS A DOMAIN RULE AND NOT THREE LINES IN A FORM ──
//
// The obvious version of "let the editor set a fee" is to write `feeAmount` and
// stop. That clears the "1 field missing — Finish setup →" nudge and is WORSE
// than the dead end it replaces, because the cost never reaches the ledger:
// Economics reads `account_adjustments`, not `extras.feeAmount`. The report
// would then show a confident ROI against a cost nobody recorded.
//
// The second-obvious version is to append the row. The old client did that, and
// it is the reason this file exists:
//
//   🔴 ONE REAL $99 APEX ACTIVATION FEE WAS RECORDED AS $89 + $99 + $99 = $287.
//
// The amount field is prefilled from the account, so correcting a fee (89 → 99)
// left BOTH numbers on the ledger, and every later edit stacked another. It
// surfaced as inflated Spend in the firm comparison — a number nobody could
// trace back to a form they had used correctly.
//
// So the rule is: AMEND the row this form owns, never append a second, and
// collapse any duplicates the old behaviour already created.
//
// ── 🚨 WHAT "THE ROW THIS FORM OWNS" MEANS, PRECISELY ──
//
// Two conditions, and the second is what makes a recurring charge safe:
//
//   1. the row's TYPE matches (Eval fee / Activation fee), AND its note looks
//      auto-generated — a note the trader typed is theirs, not this form's
//   2. for a MONTHLY fee, the DATE matches too
//
// A one-time fee is unique per account, so type alone identifies it. A monthly
// subscription legitimately recurs: last month's charge is a real, separate row
// and amending it would erase a month of spend.
//
// ── ⚠ `feeWaived` IS NOT `feeAmount === 0` ──
//
// A waived account cost nothing BY AGREEMENT; one with no fee recorded may
// simply never have had the cost entered. `domain/types.ts` argues this at
// length, and collapsing them lets missing data masquerade as a free account and
// flatter cost-per-funded. The two are mutually exclusive here: setting either
// clears the other, because a form that could leave both set would produce an
// account that is free AND costs $165.
// =============================================================================

import type { AccountAdjustment, AccountType, IsoDate } from './types';

/** What the form offers. `'free'` is the waiver. */
export type FeeCadence = 'once' | 'monthly' | 'free';

/** The auto-generated notes this form owns. A note the trader typed is theirs. */
export const AUTO_FEE_NOTE = /(activation fee|eval fee|monthly subscription)$/i;

export interface FeeState {
  feeAmount: number;
  feeCadence: string;
  feeWaived: boolean;
}

/**
 * The three stored fields, from what the form is showing.
 *
 * ⚠ MUTUALLY EXCLUSIVE. Picking "Free" on an account that already had a fee must
 * clear the amount, or the account reads as both waived and $165 — and the
 * old client's own note records what happens when it does not: the save says
 * "Saved.", the nudge still demands a cost, and there is no way to clear it.
 */
export function feeStateFrom(
  input: { amount: string | number; cadence: FeeCadence },
): FeeState {
  if (input.cadence === 'free') {
    return { feeAmount: 0, feeCadence: 'free', feeWaived: true };
  }
  // ⚠ ABSOLUTE. A fee is money out; the ledger row carries the sign, and a typed
  // "-165" must not become +165 on the ledger by double negation.
  const amount = Math.abs(Number(input.amount) || 0);
  return { feeAmount: amount, feeCadence: input.cadence, feeWaived: false };
}

/**
 * What a stored account's fee looks like in this form's terms.
 *
 * ⚠ The waiver wins. An account with `feeWaived` and a stale amount is showing
 * the amount's history, not its cost.
 */
export function feeCadenceOf(
  a: { feeWaived?: boolean; feeCadence?: string | null },
): FeeCadence {
  if (a.feeWaived === true) return 'free';
  return a.feeCadence === 'monthly' ? 'monthly' : 'once';
}

/**
 * The ledger row's type, which FOLLOWS THE ACCOUNT.
 *
 * ⚠ ONE fee field and one row type, deliberately. The old client notes there is
 * no second "activation fee" input, because a separate field asked for the same
 * money twice.
 */
export function feeTypeFor(accountType: AccountType | string): string {
  return accountType === 'live-funded' ? 'Activation fee' : 'Eval fee';
}

export function feeNoteFor(
  args: { firm: string; accountType: AccountType | string; cadence: FeeCadence },
): string {
  const firm = args.firm.trim() || 'Account';
  if (args.cadence === 'monthly') return `${firm} monthly subscription`;
  return `${firm} ${feeTypeFor(args.accountType).toLowerCase()}`;
}

export interface FeePlan {
  /** The row to correct in place, if this form already owns one. */
  amend: { id: string; amount: number; note: string } | null;
  /** The row to add, when it owns none yet. */
  insert: { date: IsoDate; type: string; amount: number; note: string } | null;
  /**
   * 🔴 Duplicates the OLD behaviour already wrote. Amending the first and
   * dropping the rest is what collapses that $287 back to $99.
   */
  remove: string[];
  /** One line for the toast, in the user's words. Null when nothing changed. */
  message: string | null;
}

/**
 * Work out what the ledger needs, without touching it.
 *
 * ⚠ TAKES `today` RATHER THAN CALLING THE CLOCK, so a monthly fee's date-matching
 * is testable at a month boundary rather than only on the day the test runs.
 */
export function planFeeAdjustment(
  args: {
    accountType: AccountType | string;
    firm: string;
    fee: FeeState;
    today: IsoDate;
    existing: readonly AccountAdjustment[];
  },
): FeePlan {
  const cadence: FeeCadence = args.fee.feeWaived
    ? 'free'
    : args.fee.feeCadence === 'monthly' ? 'monthly' : 'once';

  const type = feeTypeFor(args.accountType);
  const note = feeNoteFor({ firm: args.firm, accountType: args.accountType, cadence });
  const recurring = cadence === 'monthly';

  // The rows this form owns. See the header for why the two conditions differ.
  const owned = args.existing.filter((x) => x
    && x.type === type
    && AUTO_FEE_NOTE.test(x.note || '')
    && (!recurring || x.date === args.today));

  // ── A waived account has no fee row to keep ──
  //
  // ⚠ Only the rows this form OWNS are removed. A trader who typed their own
  // "Eval fee — paid by mistake" row keeps it; ticking Free must not delete
  // somebody's hand-entered history.
  if (args.fee.feeWaived || args.fee.feeAmount <= 0) {
    if (!owned.length) return { amend: null, insert: null, remove: [], message: null };
    return {
      amend: null,
      insert: null,
      remove: owned.map((x) => String(x.id)),
      message: args.fee.feeWaived
        ? `fee waived — ${owned.length === 1 ? 'the logged fee was' : `${owned.length} logged fees were`} removed.`
        : 'fee cleared.',
    };
  }

  const amount = -Math.abs(args.fee.feeAmount);

  if (!owned.length) {
    return {
      amend: null,
      insert: { date: args.today, type, amount, note },
      remove: [],
      message: 'fee logged.',
    };
  }

  const [first, ...rest] = owned;
  const drop = rest.map((x) => String(x.id));
  // ⚠ Nothing to say when nothing moved. A toast reading "fee updated" after a
  // save that changed only the account's name is a small lie the reader will
  // remember the next time the number looks wrong.
  const unchanged = first!.amount === amount && (first!.note || '') === note && !drop.length;

  return {
    amend: { id: String(first!.id), amount, note },
    insert: null,
    remove: drop,
    message: unchanged ? null
      : drop.length
        ? `fee corrected, ${drop.length} duplicate${drop.length === 1 ? '' : 's'} removed.`
        : 'fee updated.',
  };
}
